import React, { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { functions } from '../../config/firebase';
import {
  clearExplicitPushOptIn,
  clearPendingPushEnrollment,
  deriveProspectPushUiState,
  isIosSafariWithoutHomeScreen,
  isProspectPushOperationLive,
  readExplicitPushOptIn,
  readNotificationPermission,
  readOrCreateProspectPushInstallationId,
  readPendingPushEnrollment,
  writeExplicitPushOptIn,
} from '../../services/admin/prospectWebPushClient.js';
import {
  commitProspectPushDisable,
  commitProspectPushRegistration,
  recoverExplicitOptInFromStatus,
} from '../../services/admin/prospectWebPushEnrollment.js';
import { obtainProspectPushFcmToken, readVapidKey } from '../../services/admin/prospectWebPushMessaging.js';
import {
  getAdminProspectPushDeviceStatus,
  registerAdminProspectPushDevice,
  unregisterAdminProspectPushDevice,
} from '../../services/admin/prospectWebPushService.js';
import {
  bumpProspectPushOperationGeneration,
  bumpProspectPushSessionEpoch,
  getProspectPushOperationGeneration,
  getProspectPushSessionEpoch,
  subscribeProspectPushSessionInvalidation,
} from '../../services/admin/prospectWebPushSession.js';

/**
 * Explicit opt-in control for Lot 4B Web Push. Never auto-requests permission.
 * Survives bell close/reopen via local explicit opt-in + server status reconcile.
 * Pending enrollment identity recovers lost register responses.
 * Does not acknowledge push offers — the bell is the sole foreground consumer.
 */
export default function AdminProspectPushOptIn({ role } = {}) {
  const { t, language } = useLanguage();
  const { currentUser, impersonation, adminRoleResolved } = useAuth();
  const impersonating = impersonation?.active === true;
  const authUid = currentUser?.uid ? String(currentUser.uid) : '';

  const mountedRef = useRef(true);
  const authUidRef = useRef(authUid);
  const generationRef = useRef(getProspectPushOperationGeneration());
  const epochRef = useRef(getProspectPushSessionEpoch());
  const installationIdRef = useRef(null);
  const impersonatingRef = useRef(impersonating);
  const roleRef = useRef(role);
  const reconcileRef = useRef(null);

  authUidRef.current = authUid;
  impersonatingRef.current = impersonating;
  roleRef.current = role;

  const [permission, setPermission] = useState(() => readNotificationPermission());
  const [explicitOptIn, setExplicitOptIn] = useState(false);
  const [serverStatus, setServerStatus] = useState(null);
  const [registrationGeneration, setRegistrationGeneration] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const [errorKey, setErrorKey] = useState(null);
  const [hydrating, setHydrating] = useState(true);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const vapidConfigured = Boolean(readVapidKey());
  const iosHomeScreenHint = isIosSafariWithoutHomeScreen({
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    standalone:
      typeof window !== 'undefined'
        ? Boolean(
            window.navigator?.standalone ||
              window.matchMedia?.('(display-mode: standalone)')?.matches,
          )
        : false,
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Keep generation/epoch refs in sync when logout/account/cross-tab invalidates.
  // Settle busy/hydrating so invalidated work never leaves controls permanently stuck.
  // Ignore local-operation bumps from beginOperation — those must not clear in-flight UI.
  useEffect(() => {
    return subscribeProspectPushSessionInvalidation((snap) => {
      generationRef.current = snap.generation;
      epochRef.current = snap.epoch;
      if (snap.reason === 'local-operation') return;
      if (!mountedRef.current) return;
      setBusy(false);
      setBusyKind(null);
      setHydrating(false);
      // Reconcile after remote/local session invalidation when still mounted.
      void reconcileRef.current?.();
    });
  }, []);

  const ownership = useCallback(
    () => ({
      mounted: () => mountedRef.current,
      getActiveGeneration: () => generationRef.current,
      getActiveAuthUid: () => authUidRef.current,
      getActiveInstallationId: () => installationIdRef.current,
      eligible: () =>
        roleRef.current === 'super_admin' &&
        !impersonatingRef.current &&
        Boolean(authUidRef.current),
    }),
    [],
  );

  const beginOperation = useCallback(() => {
    const generation = bumpProspectPushOperationGeneration();
    generationRef.current = generation;
    const epoch = getProspectPushSessionEpoch();
    epochRef.current = epoch;
    return { generation, epoch };
  }, []);

  const isLive = useCallback(
    (requestGeneration, requestSessionEpoch, requestInstallationId = installationIdRef.current) =>
      isProspectPushOperationLive({
        ...ownership(),
        requestGeneration,
        requestSessionEpoch,
        requestAuthUid: authUidRef.current,
        requestInstallationId,
        getSessionEpoch: getProspectPushSessionEpoch,
      }),
    [ownership],
  );

  const applyRegisteredState = useCallback((authUidValue, installationId, registered) => {
    writeExplicitPushOptIn({
      uid: authUidValue,
      installationId,
      registrationGeneration: registered.registrationGeneration,
      enrolledAt: registered.enrolledAt,
    });
    setExplicitOptIn(true);
    setServerStatus(registered.status);
    setRegistrationGeneration(registered.registrationGeneration);
    setUnknownOutcome(false);
  }, []);

  const retryFailedCompensation = useCallback(
    async ({ pending, generation, epoch }) => {
      try {
        await unregisterAdminProspectPushDevice(
          {
            installationId: pending.installationId,
            expectedRegistrationGeneration: pending.registrationGeneration ?? undefined,
          },
          { httpsCallable, functions },
        );
        if (!isLive(generation, epoch, pending.installationId)) return { ok: false, reason: 'stale' };
        clearPendingPushEnrollment();
        setExplicitOptIn(false);
        setServerStatus('disabled');
        setRegistrationGeneration(null);
        setUnknownOutcome(false);
        return { ok: true };
      } catch {
        // Keep recoverable pending — do not abandon an active registration.
        if (isLive(generation, epoch, pending.installationId)) {
          setUnknownOutcome(true);
          setErrorKey('admin.notifications.push.error');
        }
        return { ok: false, reason: 'compensation_failed' };
      }
    },
    [isLive],
  );

  const reconcile = useCallback(async () => {
    if (role !== 'super_admin' || !authUid || impersonating) {
      setExplicitOptIn(false);
      setServerStatus(null);
      setUnknownOutcome(false);
      setHydrating(false);
      return;
    }
    const { generation, epoch } = beginOperation();
    setHydrating(true);
    setPermission(readNotificationPermission());
    const local = readExplicitPushOptIn(authUid);
    const pending = readPendingPushEnrollment(authUid);
    const installationId =
      local?.installationId ||
      pending?.installationId ||
      readOrCreateProspectPushInstallationId();
    installationIdRef.current = installationId;

    // Lost-response / failed-compensation recovery when local consent is absent.
    if (!local && pending) {
      try {
        if (pending.needsCompensation) {
          await retryFailedCompensation({ pending, generation, epoch });
          if (isLive(generation, epoch, installationId)) setHydrating(false);
          return;
        }
        const status = await getAdminProspectPushDeviceStatus(
          { installationId: pending.installationId },
          { httpsCallable, functions },
        );
        if (!isLive(generation, epoch, pending.installationId)) return;
        const recovered = recoverExplicitOptInFromStatus({
          uid: authUid,
          pending,
          status,
        });
        if (recovered?.ok) {
          applyRegisteredState(authUid, pending.installationId, recovered.registered);
          setHydrating(false);
          return;
        }
        if (recovered?.reason === 'newer_enrollment') {
          setExplicitOptIn(false);
          setServerStatus(status.status);
          setRegistrationGeneration(null);
          setUnknownOutcome(false);
          setHydrating(false);
          return;
        }
        // Uncertain — retain pending; Enable retries with the same clientOperationId.
        setExplicitOptIn(false);
        setServerStatus(status?.status ?? null);
        setUnknownOutcome(true);
        setHydrating(false);
        return;
      } catch {
        if (!isLive(generation, epoch, installationId)) return;
        setUnknownOutcome(true);
        setHydrating(false);
        return;
      }
    }

    if (!local) {
      if (!isLive(generation, epoch, installationId)) return;
      setExplicitOptIn(false);
      setServerStatus(null);
      setRegistrationGeneration(null);
      setUnknownOutcome(false);
      setHydrating(false);
      return;
    }

    setExplicitOptIn(true);
    setRegistrationGeneration(local.registrationGeneration);
    try {
      const status = await getAdminProspectPushDeviceStatus(
        { installationId },
        { httpsCallable, functions },
      );
      if (!isLive(generation, epoch, installationId)) return;
      setServerStatus(status.status);
      if (status.status === 'active') {
        setRegistrationGeneration(status.registrationGeneration);
        writeExplicitPushOptIn({
          uid: authUid,
          installationId,
          registrationGeneration: status.registrationGeneration,
          enrolledAt: status.enrolledAt,
        });
        // Renew token without permission prompt when already explicitly opted in.
        if (readNotificationPermission() === 'granted' && vapidConfigured) {
          try {
            if (!isLive(generation, epoch, installationId)) return;
            const { token } = await obtainProspectPushFcmToken();
            if (!isLive(generation, epoch, installationId)) return;
            const result = await commitProspectPushRegistration({
              intent: 'renew',
              uid: authUid,
              installationId,
              token,
              locale: language === 'en' ? 'en' : 'fr',
              expectedRegistrationGeneration: status.registrationGeneration,
              requestGeneration: generation,
              requestSessionEpoch: epoch,
              ownership: ownership(),
              register: registerAdminProspectPushDevice,
              unregister: unregisterAdminProspectPushDevice,
              getStatus: getAdminProspectPushDeviceStatus,
              registerDeps: { httpsCallable, functions },
            });
            if (!isLive(generation, epoch, installationId)) return;
            if (result.ok) {
              applyRegisteredState(authUid, installationId, result.registered);
            } else if (result.reason === 'renew_stale' && result.registered) {
              setRegistrationGeneration(result.registered.registrationGeneration);
            }
          } catch {
            /* keep prior server status; user can retry Enable */
          }
        }
      } else if (status.status === 'disabled' || status.status === 'missing') {
        clearExplicitPushOptIn();
        clearPendingPushEnrollment();
        setExplicitOptIn(false);
        setServerStatus(status.status);
        setRegistrationGeneration(null);
      }
    } catch {
      if (!isLive(generation, epoch, installationId)) return;
      setErrorKey('admin.notifications.push.error');
    } finally {
      if (isLive(generation, epoch, installationId)) setHydrating(false);
    }
  }, [
    applyRegisteredState,
    authUid,
    beginOperation,
    impersonating,
    isLive,
    language,
    ownership,
    retryFailedCompensation,
    role,
    vapidConfigured,
  ]);

  reconcileRef.current = reconcile;

  useEffect(() => {
    if (!adminRoleResolved) return undefined;
    void reconcile();
    return undefined;
  }, [adminRoleResolved, authUid, role, impersonating, reconcile]);

  const ui = deriveProspectPushUiState({
    role,
    impersonating,
    permission,
    explicitOptIn,
    serverStatus,
    busy: busy || hydrating,
    busyKind,
    errorKey,
    iosHomeScreenHint,
    vapidConfigured,
    unknownOutcome,
  });

  const enable = useCallback(async () => {
    if (busy || impersonating || role !== 'super_admin' || !authUid) return;
    const { generation, epoch } = beginOperation();
    setBusy(true);
    setBusyKind('enable');
    setErrorKey(null);
    try {
      const pending = readPendingPushEnrollment(authUid);
      if (pending?.needsCompensation) {
        await retryFailedCompensation({ pending, generation, epoch });
        return;
      }

      // Prefer status recovery for an uncertain enroll before a fresh register.
      if (pending && !pending.needsCompensation) {
        try {
          const status = await getAdminProspectPushDeviceStatus(
            { installationId: pending.installationId },
            { httpsCallable, functions },
          );
          if (!isLive(generation, epoch, pending.installationId)) return;
          const recovered = recoverExplicitOptInFromStatus({
            uid: authUid,
            pending,
            status,
          });
          if (recovered?.ok) {
            installationIdRef.current = pending.installationId;
            applyRegisteredState(authUid, pending.installationId, recovered.registered);
            return;
          }
          if (recovered?.reason === 'newer_enrollment') {
            setExplicitOptIn(false);
            setServerStatus(status.status);
            setUnknownOutcome(false);
            setErrorKey('admin.notifications.push.error');
            return;
          }
        } catch {
          /* fall through to idempotent enroll retry with same clientOperationId */
        }
      }

      if (typeof Notification === 'undefined' || typeof Notification.requestPermission !== 'function') {
        if (isLive(generation, epoch)) setPermission('unsupported');
        return;
      }
      // Explicit user gesture only — never auto-prompt.
      const next = await Notification.requestPermission();
      if (!isLive(generation, epoch)) return;
      setPermission(next);
      if (next !== 'granted') return;
      if (!isLive(generation, epoch)) return;
      const { token } = await obtainProspectPushFcmToken();
      if (!isLive(generation, epoch)) return;
      const installationId =
        pending?.installationId || readOrCreateProspectPushInstallationId();
      installationIdRef.current = installationId;
      const result = await commitProspectPushRegistration({
        intent: 'enroll',
        uid: authUid,
        installationId,
        token,
        locale: language === 'en' ? 'en' : 'fr',
        // Idempotent retry of the same pending enrollment — never a blind new enroll.
        clientOperationId: pending?.clientOperationId || null,
        requestGeneration: generation,
        requestSessionEpoch: epoch,
        ownership: ownership(),
        register: registerAdminProspectPushDevice,
        unregister: unregisterAdminProspectPushDevice,
        getStatus: getAdminProspectPushDeviceStatus,
        registerDeps: { httpsCallable, functions },
      });
      if (!isLive(generation, epoch, installationId)) return;
      if (result.reason === 'unknown_outcome') {
        setUnknownOutcome(true);
        setErrorKey('admin.notifications.push.error');
        return;
      }
      if (result.reason === 'newer_enrollment') {
        setUnknownOutcome(false);
        setErrorKey('admin.notifications.push.error');
        return;
      }
      if (!result.ok) {
        setErrorKey('admin.notifications.push.error');
        return;
      }
      applyRegisteredState(authUid, installationId, result.registered);
    } catch {
      if (isLive(generation, epoch)) setErrorKey('admin.notifications.push.error');
    } finally {
      // Clear busy only while this operation still owns the generation, or when
      // the invalidation listener has not already settled controls.
      if (mountedRef.current && generationRef.current === generation) {
        setBusy(false);
        setBusyKind(null);
      }
    }
  }, [
    applyRegisteredState,
    authUid,
    beginOperation,
    busy,
    impersonating,
    isLive,
    language,
    ownership,
    retryFailedCompensation,
    role,
  ]);

  const disable = useCallback(async () => {
    if (busy || impersonating || role !== 'super_admin' || !authUid) return;
    const { generation, epoch } = beginOperation();
    setBusy(true);
    setBusyKind('disable');
    setErrorKey(null);
    try {
      const local = readExplicitPushOptIn(authUid);
      const installationId =
        local?.installationId ||
        installationIdRef.current ||
        readOrCreateProspectPushInstallationId();
      installationIdRef.current = installationId;
      // Always prefer the freshest known generation (reconciled after prior stale).
      const expected =
        registrationGeneration ?? local?.registrationGeneration ?? null;
      const result = await commitProspectPushDisable({
        uid: authUid,
        installationId,
        expectedRegistrationGeneration: expected,
        requestGeneration: generation,
        requestSessionEpoch: epoch,
        ownership: ownership(),
        unregister: unregisterAdminProspectPushDevice,
        unregisterDeps: { httpsCallable, functions },
      });
      if (!isLive(generation, epoch, installationId)) return;
      if (result.reason === 'disable_conflict') {
        // Reconcile current generation — do not retry the same stale generation.
        const currentGen = result.currentRegistrationGeneration;
        if (currentGen != null) setRegistrationGeneration(currentGen);
        try {
          const status = await getAdminProspectPushDeviceStatus(
            { installationId },
            { httpsCallable, functions },
          );
          if (!isLive(generation, epoch, installationId)) return;
          setServerStatus(status.status);
          if (status.registrationGeneration != null) {
            setRegistrationGeneration(status.registrationGeneration);
          }
          if (status.status === 'active') {
            writeExplicitPushOptIn({
              uid: authUid,
              installationId,
              registrationGeneration: status.registrationGeneration,
              enrolledAt: status.enrolledAt,
            });
            setExplicitOptIn(true);
          }
        } catch {
          /* still offer retry with whatever generation we have */
        }
        setErrorKey('admin.notifications.push.disableError');
        return;
      }
      if (!result.ok) {
        setErrorKey('admin.notifications.push.disableError');
        return;
      }
      setExplicitOptIn(false);
      setServerStatus('disabled');
      setRegistrationGeneration(null);
      setUnknownOutcome(false);
      // Notify other tabs; same-tab listener settles busy/hydrating.
      bumpProspectPushSessionEpoch();
    } catch {
      if (isLive(generation, epoch)) setErrorKey('admin.notifications.push.disableError');
    } finally {
      if (mountedRef.current && generationRef.current === generation) {
        setBusy(false);
        setBusyKind(null);
      }
    }
  }, [
    authUid,
    beginOperation,
    busy,
    impersonating,
    isLive,
    ownership,
    registrationGeneration,
    role,
  ]);

  if (ui.kind === 'hidden') return null;

  return (
    <div className="border-b border-slate-100 px-3 py-2" data-testid="admin-prospect-push-opt-in">
      {ui.kind === 'impersonation_blocked' ? (
        <p className="text-xs text-amber-800">{t('admin.notifications.push.impersonationBlocked')}</p>
      ) : null}
      {ui.kind === 'unavailable' ? (
        <p className="text-xs text-slate-600">{t('admin.notifications.push.unavailable')}</p>
      ) : null}
      {ui.kind === 'unsupported' ? (
        <p className="text-xs text-slate-600">{t('admin.notifications.push.unsupported')}</p>
      ) : null}
      {ui.kind === 'ios_home_screen' ? (
        <p className="text-xs text-slate-600">{t('admin.notifications.push.iosHomeScreen')}</p>
      ) : null}
      {ui.kind === 'denied' ? (
        <p className="text-xs text-slate-600">{t('admin.notifications.push.denied')}</p>
      ) : null}
      {ui.kind === 'enabled' || ui.kind === 'busy_disable' || ui.kind === 'disable_error' ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs text-emerald-800">{t('admin.notifications.push.enabled')}</p>
          <button
            type="button"
            className="text-xs font-medium text-slate-600 underline-offset-2 hover:underline disabled:opacity-50"
            disabled={busy}
            onClick={() => void disable()}
          >
            {busyKind === 'disable'
              ? t('admin.notifications.push.busy')
              : t('admin.notifications.push.disable')}
          </button>
        </div>
      ) : null}
      {ui.kind === 'granted_unregistered' ||
      ui.kind === 'opt_in' ||
      ui.kind === 'opt_in_reconcile' ||
      ui.kind === 'busy' ||
      ui.kind === 'error' ||
      ui.kind === 'unknown_outcome' ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            disabled={busy || hydrating}
            onClick={() => void enable()}
          >
            {busyKind === 'enable'
              ? t('admin.notifications.push.busy')
              : t('admin.notifications.push.enable')}
          </button>
          <p className="text-xs text-slate-500">{t('admin.notifications.push.hint')}</p>
        </div>
      ) : null}
      {errorKey ? (
        <div className="mt-1" role="alert">
          <p className="text-xs text-rose-700">{t(errorKey)}</p>
          {errorKey === 'admin.notifications.push.disableError' ? (
            <button
              type="button"
              className="mt-1 text-xs font-medium text-indigo-700 underline-offset-2 hover:underline"
              onClick={() => void disable()}
            >
              {t('admin.notifications.push.disableRetry')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
